import { useState } from "react";
import { ArrowUpRight, Loader2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../lib/site";
import { Reveal } from "./Reveal";

const SERVICE_OPTIONS = [
  "Excavation & Earthworks",
  "Foundations & Concrete",
  "Drainage & Soakaways",
  "Drain Repairs & CCTV",
  "Site Clearance & Prep",
  "Something else",
];

const inputCls =
  "w-full border border-line bg-coal-900 px-4 py-3.5 text-sm text-white placeholder:text-steel/50 outline-none transition-colors duration-300 focus:border-ember rounded-none";

export const QuoteSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success("Quote request received — we'll be in touch within one working day.");
      setForm({ name: "", phone: "", email: "", service: SERVICE_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="border-t border-line px-5 py-24 md:px-12 md:py-32">
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ember">[ Free quotes ]</p>
            <h2 className="font-display text-5xl uppercase leading-[0.85] tracking-tight text-white md:text-7xl">
              Tell us<br />about<br />the job
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-steel md:text-base">
              Send the details and we'll come back with a fixed, no-obligation
              quote — usually after a free site visit. Prefer to talk it through?
              Ask for Mr Fox.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="quote-phone-button"
                className="group flex items-center justify-center gap-3 border border-white/25 px-6 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-ember hover:text-ember"
              >
                <Phone size={14} className="text-ember" /> {PHONE_DISPLAY}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-whatsapp-button"
                className="group relative flex items-center justify-center gap-3 overflow-hidden border border-ember px-6 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ember"
              >
                <span className="absolute inset-0 translate-y-full bg-ember transition-transform duration-300 ease-out group-hover:translate-y-0" aria-hidden="true" />
                <span className="relative flex items-center gap-3 transition-colors duration-300 group-hover:text-coal-900">
                  <MessageCircle size={14} /> WhatsApp us
                </span>
              </a>
            </div>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
              Mon–Sat · 7:30–17:30 · We respond within one working day
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="border border-line bg-coal-800 p-6 md:p-10"
          >
            <p className="mb-8 font-mono text-[11px] uppercase tracking-[0.25em] text-steel">Request form — 01</p>
            <div className="space-y-5">
              <div>
                <label htmlFor="quote-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Name *</label>
                <input
                  id="quote-name"
                  data-testid="quote-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="quote-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Phone *</label>
                <input
                  id="quote-phone"
                  data-testid="quote-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Best number to reach you"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="quote-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Email — for confirmation</label>
                <input
                  id="quote-email"
                  data-testid="quote-email-input"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com (optional)"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="quote-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Service</label>
                <select
                  id="quote-service"
                  data-testid="quote-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className={`${inputCls} appearance-none`}
                >
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-coal-900">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quote-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">The job *</label>
                <textarea
                  id="quote-message"
                  data-testid="quote-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Where's the job, roughly what's needed, and when you'd like it done."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="quote-submit-button"
                disabled={sending}
                className="group relative flex w-full items-center justify-center gap-3 overflow-hidden bg-ember px-6 py-4 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-coal-900 transition-opacity disabled:opacity-60"
              >
                <span className="absolute inset-0 translate-y-full bg-white transition-transform duration-300 ease-out group-hover:translate-y-0" aria-hidden="true" />
                <span className="relative flex items-center gap-2">
                  {sending ? (
                    <>Sending <Loader2 size={14} className="animate-spin" /></>
                  ) : (
                    <>Send request <ArrowUpRight size={14} /></>
                  )}
                </span>
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
